import { CustomField } from 'src/app/+shared/custom-fields/models';
import { CustomFieldConfig } from 'src/app/+shared/custom-fields/models/customFieldConfig';
import { AllLabels, ApplicationSectionKeys, InstallationFlowType } from 'src/app/constants/app.constants';
import {
  personalDataCustomFields,
  districtHeatDeviceCustomFields,
  gasDeviceCustomFields,
  waterDeviceCustomFields,
} from 'src/app/+shared/custom-fields/common-custom-fields';
import { CommonService } from 'src/app/services/common.service';
import { CustomFieldType } from 'src/app/+shared/custom-fields/models/enums/customFieldType';
import { HeatingDeviceObject, WaterDeviceObject } from 'src/app/jurisdiction/facilities-form/facilities-form.domain';
import { AllDropdownKeys } from 'src/app/jurisdiction/jurisdiction.constants';
import { LocalStorageService } from 'src/app/services/local-storage.service';

const personalDataFields: CustomField[] = [{ ...personalDataCustomFields.addressSuffix, row: 1 }];

const districtHeatDeviceFields: CustomField[] = [
  { ...districtHeatDeviceCustomFields.districtHeatType, row: 1 },
  {
    name: 'installDirections',
    label: 'facilities-data.form-fields.install-directions',
    section: ApplicationSectionKeys.technicalData.districtHeatDevice,
    type: CustomFieldType.Text,
    row: 1,
  },
  { ...districtHeatDeviceCustomFields.heatingLoad, row: 2 },
  { ...districtHeatDeviceCustomFields.installHeatPower, row: 2 },
  { ...districtHeatDeviceCustomFields.dhw, row: 4, required: false },
  { ...districtHeatDeviceCustomFields.designTempTv, row: 4, hint: null, tooltip: 'facilities-data.form-fields.tv_description' },
  {
    name: 'dhwType',
    type: CustomFieldType.CheckOptions,
    label: 'sk_swkn_districthead_system_type',
    emptyLabel: true,
    row: 5,
    section: ApplicationSectionKeys.technicalData.districtHeatDevice,
    displayFunction: (value: HeatingDeviceObject) => value.dhw,
    checkOptionsData: [
      { label: 'lookup.sk_swkn_districthead_system_type.options.storage_charging_system', value: 'storage_charging_system' },
      { label: 'lookup.sk_swkn_districthead_system_type.options.flow_heater', value: 'flow_heater' },
    ],
  },
  {
    ...districtHeatDeviceCustomFields.designTempTr,
    row: 6,
    hint: null,
    tooltip: 'facilities-data.form-fields.tr_description',
  },
];

const technicalDataFields: CustomField[] = [...districtHeatDeviceFields];

const gasDeviceFields: CustomField[] = [
  { ...gasDeviceCustomFields.ceNumber, row: 1 },
  { ...gasDeviceCustomFields.deviceRemovedCheckbox, row: 1, displayFunction: (_, isReadonly: boolean) => !isReadonly },
  { ...gasDeviceCustomFields.deviceName, row: 2 },
  { ...gasDeviceCustomFields.power, row: 2 },
  { ...gasDeviceCustomFields.selectedGasCategory, row: 10, label: 'facilities-data.form-fields.selected-gas-category' },
];

export const configs: CustomFieldConfig[] = Object.values(InstallationFlowType).map(applicationType => {
  const isWaterFacilitySelected = (localStorageService: LocalStorageService) => {
    let facilityTypes = localStorageService.getItem('selected_facilities');
    return facilityTypes?.length && facilityTypes[0]?.key === 'water';
  };

  const installationApplicationTypes = [InstallationFlowType.NewInstallation, InstallationFlowType.UpdateInstallation];

  let isInstallationApplication: boolean = installationApplicationTypes.includes(applicationType);

  const applicationSpecificFields: CustomField[] = [
    {
      ...waterDeviceCustomFields.specialApplianceDropDown,
      dropdownTypeKey: isInstallationApplication ? AllDropdownKeys.TappingPointTypeNew : AllDropdownKeys.TappingPointType,
      required: true,
      row: 1,
    },
  ];

  if (!isInstallationApplication) {
    applicationSpecificFields.push(
      { ...waterDeviceCustomFields.amount, row: 1 },
      { ...waterDeviceCustomFields.vrCold, row: 2 },
      { ...waterDeviceCustomFields.vrHot, row: 2 }
    );
  } else {
    applicationSpecificFields.push(
      {
        name: 'swknDWGVCertifiedCheckbox',
        label: 'facilities-data.form-fields.swkn-dwgv-certified',
        emptyLabel: true,
        section: ApplicationSectionKeys.technicalData.waterDevice,
        type: CustomFieldType.Checkbox,
        row: 1,
      },
      {
        name: 'swknManufacturer',
        label: 'facilities-data.form-fields.swkn-manufacturer',
        section: ApplicationSectionKeys.technicalData.waterDevice,
        type: CustomFieldType.Text,
        row: 2,
        requiredFunction: (value: WaterDeviceObject) => value?.specialApplianceDropDown === 'pressurizer',
      },
      {
        name: 'swknType',
        label: 'facilities-data.form-fields.type',
        section: ApplicationSectionKeys.technicalData.waterDevice,
        type: CustomFieldType.Text,
        row: 2,
        requiredFunction: (value: WaterDeviceObject) => value?.specialApplianceDropDown === 'pressurizer',
      },
      {
        name: 'swknFlowRate',
        label: 'facilities-data.form-fields.swkn-flow-rate',
        section: ApplicationSectionKeys.technicalData.waterDevice,
        type: CustomFieldType.Number,
        row: 3,
        units: AllLabels.units.volumeFlowRate,
        displayFunction: (value: WaterDeviceObject, _ro: boolean, _cs: CommonService, localStorageService: LocalStorageService) =>
          value?.specialApplianceDropDown === 'pressurizer' && isWaterFacilitySelected(localStorageService),
      },
      {
        name: 'swknDeliveryPressure',
        label: 'facilities-data.form-fields.swkn-delivery-pressure',
        section: ApplicationSectionKeys.technicalData.waterDevice,
        type: CustomFieldType.Text,
        row: 3,
        units: AllLabels.units.deliveryPressure,
        displayFunction: (value: WaterDeviceObject, _ro: boolean, _cs: CommonService, localStorageService: LocalStorageService) =>
          value?.specialApplianceDropDown === 'pressurizer' && isWaterFacilitySelected(localStorageService),
      },
      {
        name: 'swknWaterFacilitySystemType',
        label: 'facilities-data.form-fields.water-system',
        section: ApplicationSectionKeys.technicalData.waterDevice,
        type: CustomFieldType.CheckOptions,
        row: 4,
        defaultValue: 'sprinkler',
        displayFunction: (value: WaterDeviceObject, _ro: boolean, _cs: CommonService, localStorageService: LocalStorageService) =>
          value?.specialApplianceDropDown === 'extinguisher' && isWaterFacilitySelected(localStorageService),
        checkOptionsData: [
          { label: 'lookup.sk_swkn_water_facility_system_type-new.options.sprinkler.name', value: 'sprinkler' },
          { label: 'lookup.sk_swkn_water_facility_system_type-new.options.hydrant.name', value: 'hydrant' },
        ],
      },
      {
        name: 'swknReqExtinguishingVolume',
        label: 'facilities-data.form-fields.swkn-req-extinguishing-volume',
        section: ApplicationSectionKeys.technicalData.waterDevice,
        type: CustomFieldType.Number,
        row: 4,
        units: AllLabels.units.volumeFlowRate,
        displayFunction: (value: WaterDeviceObject, _ro: boolean, _cs: CommonService, localStorageService: LocalStorageService) =>
          value?.specialApplianceDropDown === 'extinguisher' && isWaterFacilitySelected(localStorageService),
      }
    );
  }

  return {
    applicationType,
    fields: [...personalDataFields, ...technicalDataFields, ...gasDeviceFields, ...applicationSpecificFields],
  };
});
